import { addMonths, addWeeks, endOfMonth, format, startOfMonth, startOfWeek, subDays, subMonths } from "date-fns";
import { uniq } from "lodash";
import { ReactNode } from "react";

import { DateFormat } from "constants/date";
import { CustomDateRangeFilterId } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { recursLabels } from "constants/reports/recurs";
import { ReportType } from "constants/reports/report-type";
import { sourceLabels } from "constants/reports/source";
import { ToastContent, getToastContent } from "constants/toasts";
import { round } from "helpers/numbers";
import { deepMerge } from "helpers/object";
import { numberSort } from "helpers/sort";
import { addLeadingZero } from "helpers/time";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { getWeekDayLabelsFromPeriod } from "routes/reports/helpers";

import { IChartData, IChartSeries, IChartSummary, IReportDataSet, ReportFetchFailureReason } from "./interfaces";

/**
 * Format time to max 3 first units Ex. 3d 20h 50m, 5h 20m 30s
 * @param hoursSource hours with seconds and minutes in float - ex. 3.5 = 3h 30m
 */
export function formatTime(hoursSource: number): string {
  let days = Math.floor(hoursSource / 24);
  let hours = Math.floor(hoursSource % 24);
  const minutesPart = Number((hoursSource % 1).toFixed(6));
  let minutes = Math.floor(minutesPart * 60);
  const secondsPart = Number(((minutesPart * 60) % 1).toFixed(6));
  let seconds = Math.round(secondsPart * 60);
  const elements = [];

  if (seconds === 60) {
    minutes += 1;
    seconds = 0;
  }

  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }

  if (hours === 24) {
    days += 1;
    hours = 0;
  }

  if (days) {
    elements.push(`${days}d`);
  }

  if (hours || days) {
    elements.push(`${hours}h`);
  }

  if (minutes || (minutes && seconds)) {
    elements.push(`${minutes}min`);
  }

  if (seconds || !hoursSource) {
    elements.push(`${seconds}s`);
  }

  return elements.slice(0, 3).join(" ");
}

/**
 * Format time to max hours and minutes
 * @param hoursSource hours with seconds and minutes in float - ex. 3.5 = 3h 30m
 */
export function formatTimeToHours(hoursSource: number): string {
  let hours = Math.floor(hoursSource);
  const minutesPart = Number((hoursSource % 1).toFixed(6));
  let minutes = Math.floor(minutesPart * 60);
  const secondsPart = Number(((minutesPart * 60) % 1).toFixed(6));
  const seconds = Math.round(secondsPart * 60);
  const elements = [];

  if (seconds > 30) {
    minutes += 1;
  }

  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }

  if (hours || (!hours && !minutes)) {
    elements.push(`${hours}h`);
  }

  if (minutes) {
    elements.push(`${minutes}min`);
  }

  return elements.join(" ");
}

export function calculateSatisfaction(good: number, bad: number): number {
  const avg = Math.round((good / (good + bad)) * 100);
  // @ts-expect-error TS(2322): Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
  return !isNaN(avg) ? avg : null;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getFormatedPercent(value: any, precision = 0): string {
  if (Number.isNaN(value)) {
    return "0%";
  }
  return `${round(value * 100, precision)}%`;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
export function sumValueFromPeriod(period: any, ...keys: any[]) {
  return Object.keys(period).reduce(
    (acc, curr) => acc + keys.reduce((sum, key) => sum + (period[curr][key] || 0), 0),
    0
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
export function avgValueFromPeriod(period: any, key: any) {
  const principles = Object.keys(period).reduce(
    (acc, curr) => {
      if (period[curr][key]) {
        acc.value += period[curr][key] || 0;
        acc.count += 1;
      }
      return acc;
    },
    { value: 0, count: 0 }
  );
  return principles.value / principles.count || null;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
export function getSeriesDataFromPeriod(period: any, key: any, distribution: ReportDistribution) {
  if (distribution === ReportDistribution.Hour) {
    return Object.keys(period)
      .map(value => Number(value))
      .sort(numberSort)
      .map(value => period[value][key]);
  }

  return Object.keys(period)
    .sort()
    .map(value => period[value][key]);
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
export function getLabelsFromPeriod(period: any, distribution: ReportDistribution) {
  if (distribution === ReportDistribution.Hour) {
    return Object.keys(period)
      .map(label => addLeadingZero(label))
      .sort();
  }
  return Object.keys(period).sort();
}

export function getRecursFromPeriod(period: string): string {
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return recursLabels[period] ? recursLabels[period] : period;
}

export function getSourceFromType(type: string): string {
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return sourceLabels[type] ? sourceLabels[type] : type;
}

export function getDateRangeFromPeriod(period: string): {
  dateFrom: string;
  dateTo: string;
  value: string;
  label?: string;
} {
  const dateRange = {
    dateFrom: "",
    dateTo: "",
    value: "",
    label: "",
  };
  const now = new Date();
  const dateFormat = DateFormat.HtmlInputDate;

  if (period === CustomDateRangeFilterId.Yesterday) {
    const yesterday = subDays(now, 1);
    dateRange.dateFrom = format(yesterday, dateFormat);
    dateRange.dateTo = format(yesterday, dateFormat);
    dateRange.value = CustomDateRangeFilterId.Yesterday;
  }

  if (period === CustomDateRangeFilterId.Last7Days) {
    const startWeek = subDays(now, 7);
    const endWeek = subDays(now, 1);
    const dateFrom = `${getWeekDayLabelsFromPeriod(startWeek)}, ${startWeek.getDate()} ${format(
      startWeek,
      "MMM"
    )} ${startWeek.getFullYear()}`;
    const dateTo = `${getWeekDayLabelsFromPeriod(endWeek)}, ${endWeek.getDate()} ${format(
      endWeek,
      "MMM"
    )} ${endWeek.getFullYear()}`;
    dateRange.dateFrom = format(startWeek, dateFormat);
    dateRange.dateTo = format(endWeek, dateFormat);
    dateRange.value = CustomDateRangeFilterId.Last7Days;
    dateRange.label = `${dateFrom} - ${dateTo}`;
  }

  if (period === CustomDateRangeFilterId.LastMonth) {
    const startMonth = startOfMonth(subMonths(now, 1));
    const endMonth = endOfMonth(subMonths(now, 1));
    dateRange.dateFrom = format(startMonth, dateFormat);
    dateRange.dateTo = format(endMonth, dateFormat);
    dateRange.value = CustomDateRangeFilterId.LastMonth;
    dateRange.label = `${format(startMonth, "MMM")} ${startMonth.getFullYear()}`;
  }

  return dateRange;
}

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function getMonthName(monthNumber: number): string {
  return months[(monthNumber % 12) - 1];
}

export function getNextReportFromPeriod(period: string): string {
  if (period === "day") {
    return "tomorrow";
  }

  let nextReport = "";
  const now = new Date();

  if (period === "week") {
    const week = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
    nextReport = `${week.getDate()} ${months[week.getMonth()]} ${week.getFullYear()}`;
  }

  if (period === "month") {
    const month = startOfMonth(addMonths(now, 1));
    nextReport = `${month.getDate()} ${months[month.getMonth()]} ${month.getFullYear()}`;
  }

  return nextReport;
}

export function getReportId(type: string, period: string): string {
  return `${type}-${period}`;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isChartData(data: any): data is IChartData {
  return Array.isArray(data.summary) && Array.isArray(data.series) && Array.isArray(data.labels);
}

/**
 * Merge multiple series x labels for chart and make a unique values of them
 * to avoid duplication of the same period labels
 * @param series
 */
export function labelsMerge(series: (string[] | string)[][]): string[][] {
  return series
    .map(labels => labels.map(label => (Array.isArray(label) ? label : [label])))
    .sort((a, b) => {
      if (a.length === b.length) {
        return 0;
      }
      return a.length < b.length ? 1 : -1;
    })
    .reduce((acc, arr) => {
      arr.forEach((el, index) => {
        if (!acc[index]) {
          acc[index] = [];
        }

        acc[index].push(...el);
      });
      return acc;
    }, [])
    .map(labels => uniq(labels));
}

function parseSummariesFromDataSets(dataSets: IReportDataSet[], fieldKey: string): IChartSummary[] {
  return dataSets.reduce((summaries, set) => {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newSummaries = set.data[fieldKey].summary.map((summary: any) => ({
      ...summary,
      filters: set.filters,
    }));

    return summaries.concat(...newSummaries);
  }, []);
}

function parseLabelsFromDataSets(dataSets: IReportDataSet[], fieldKey: string): string[][] {
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return labelsMerge(dataSets.map(set => set.data[fieldKey].labels));
}

function parseSeriesFromDataSets(dataSets: IReportDataSet[], fieldKey: string): IChartSeries[] {
  return dataSets.reduce(
    (seriesList, set) =>
      seriesList.concat(
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...set.data[fieldKey].series.map((series: any) => ({
          ...series,
          filters: set.filters,
        }))
      ),
    []
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseChartData(dataSets: IReportDataSet[]): any {
  const chartKeys: string[] = uniq(
    dataSets.reduce((acc, set) => {
      // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
      acc.push(...Object.keys(set.data).filter(key => isChartData(set.data[key])));
      return acc;
    }, [])
  );

  const chartData = chartKeys.reduce((acc, key) => {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    acc[key] = {
      summary: parseSummariesFromDataSets(dataSets, key),
      labels: parseLabelsFromDataSets(dataSets, key),
      series: parseSeriesFromDataSets(dataSets, key),
    };
    return acc;
  }, {});

  return deepMerge({}, ...dataSets.map(set => set.data).reverse(), chartData);
}

// FIXME: Fix this the next time the file is edited.
// @ts-expect-error TS(6133): 'reportType' is declared but its value is never re... Remove this comment to see the full error message
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function isReportTypeIsDisabled(reportType: ReportType): boolean {
  return false;
}

export const mapFetchFailureReasonToMessage = (reason: ReportFetchFailureReason, message: string): ReactNode => {
  switch (reason) {
    case ReportFetchFailureReason.DateRangeTooLong: {
      return message;
    }
    case ReportFetchFailureReason.TooManyTags: {
      return getToastContent(ToastContent.REPORT_TOO_MUCH_TAGS);
    }
    case ReportFetchFailureReason.TooManyAgents: {
      return getToastContent(ToastContent.REPORT_TOO_MUCH_AGENTS);
    }

    case ReportFetchFailureReason.TooManyTeams: {
      return getToastContent(ToastContent.REPORT_TOO_MUCH_TEAMS);
    }
    default:
      return message;
  }
};
